import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Target, MessageCircle, Menu, X, PlusCircle, BookOpen, Activity, BarChart3 } from 'lucide-react';
import axios from '../config/axios';
import conversationService from '../services/conversations';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const EmitDashboard = () => {
  // Tab state - NOW WITH 3 TABS
  const [activeTab, setActiveTab] = useState('progress');
  
  // Social sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [shareStyle, setShareStyle] = useState('insight');
  
  // Progress dashboard state
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chat state
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatError, setChatError] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [queryCategory, setQueryCategory] = useState('general');
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [latestEmitMessageId, setLatestEmitMessageId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Practice Logger state
  const [practiceForm, setPracticeForm] = useState({
    mindstream_type: 'power',
    practice_type: '',
    practice_name: '',
    duration_minutes: 15,
    quality_rating: 7,
    insights_gained: ''
  });
  const [isSubmittingPractice, setIsSubmittingPractice] = useState(false);
  const [practiceSuccess, setPracticeSuccess] = useState('');
  const [practiceError, setPracticeError] = useState('');
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const latestEmitMessageRef = useRef(null);
  
  const apiUrl = window.location.origin;

  // Category definitions
  const categories = [
    { 
      value: 'general', 
      label: '💬 General Guidance', 
      icon: '💬',
      description: 'Open dialogue about any topic'
    },
    { 
      value: 'biblical', 
      label: '📖 Biblical Study', 
      icon: '📖',
      description: 'Scripture analysis through Fhoism lens (9-section format)'
    },
    { 
      value: 'self-development', 
      label: '🌱 Self-Development', 
      icon: '🌱',
      description: 'Personal growth & transformation steps'
    },
    { 
      value: 'mindstreams', 
      label: '🧠 MindStreams', 
      icon: '🧠',
      description: 'Power, Flow & Clarity practices'
    }
  ];

  // MindStream definitions for practice logger
  const mindstreamOptions = [
    {
      value: 'power',
      label: 'Power (I-THEE)',
      color: 'primary',
      description: 'Divine connection, spiritual study, prayer',
      examples: 'Bible study, meditation on scripture, worship'
    },
    {
      value: 'flow',
      label: 'Flow (I-ME)',
      color: 'danger',
      description: 'Self-awareness, inner peace, emotional clarity',
      examples: 'Meditation, journaling, gratitude practice'
    },
    {
      value: 'clarity',
      label: 'Clarity (I-WE)',
      color: 'success',
      description: 'Service, community, helping others',
      examples: 'Community service, helping neighbors, outreach'
    }
  ];

  // Common practice types
  const practiceTypes = {
    power: [
      'Bible Study',
      'Scripture Meditation',
      'Prayer Session',
      'Worship',
      'Theological Study',
      'Sermon Notes',
      'Other'
    ],
    flow: [
      'Meditation',
      'Gratitude Journaling',
      'Mindfulness Practice',
      'Breathwork',
      'Self-Reflection',
      'Emotional Processing',
      'Other'
    ],
    clarity: [
      'Community Service',
      'Helping Others',
      'Church Outreach',
      'Mentoring',
      'Teaching',
      'Volunteering',
      'Other'
    ]
  };

  const currentCategory = categories.find(c => c.value === queryCategory);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch FHO dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/fho/dashboard/test_user_123`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      
      const transformedData = {
        profile: {
          username: data.profile?.username || 'User',
          email: data.profile?.email || 'user@emit.com',
          consciousness_points: data.profile?.consciousness_points || 0
        },
        current_stage: {
          stage_number: data.profile?.consciousness_level || 1,
          stage_name: data.profile?.journey_stage || 'Beginning Journey'
        },
        progress: {
          total_practice_sessions: data.profile?.total_practices || 0,
          days_active: data.profile?.current_streak || 0,
          streak_days: data.profile?.current_streak || 0
        },
        mindstreams: (data.mindstreams || []).map(stream => ({
          name: stream.stream_type === 'power' ? 'Power (I-THEE)' : 
                stream.stream_type === 'flow' ? 'Flow (I-ME)' : 'Clarity (I-WE)',
          type: stream.stream_type,
          level: stream.level,
          score: Math.round(stream.progress_percentage || 0),
          sessions: stream.sessions_completed || 0,
          time_minutes: stream.total_time_minutes || 0,
          streak: stream.current_streak || 0
        })),
        dimensions: (data.das_abilities || []).map(ability => ({
          name: ability.ability_name,
          power: ability.power_name,
          mindstream: ability.das_type,
          score: Math.round(ability.mastery_percentage || 0),
          times_practiced: ability.times_practiced || 0,
          is_active: ability.is_active
        })),
        recent_practices: (data.recent_practices || []).length > 0 
          ? data.recent_practices.map(practice => ({
              practice_type: practice.practice_name,
              notes: practice.insights_gained || 'No notes',
              duration_minutes: practice.duration_minutes,
              impact_score: practice.quality_rating,
              practice_date: practice.practiced_at
            }))
          : data.mindstreams
              .filter(s => s.last_session)
              .map(stream => ({
                practice_type: `${stream.stream_type.charAt(0).toUpperCase() + stream.stream_type.slice(1)} Practice`,
                notes: `${stream.sessions_completed} sessions completed`,
                duration_minutes: Math.round(stream.total_time_minutes / Math.max(stream.sessions_completed, 1)),
                impact_score: Math.round(stream.average_session_quality || 0),
                practice_date: stream.last_session
              }))
      };
      
      setUserData(transformedData);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Helper functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateId = () => {
    return 'temp_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const generateTitleFromMessage = (message) => {
    const words = message.split(' ');
    return words.slice(0, 5).join(' ') + (words.length > 5 ? '...' : '');
  };

  const cleanSystemTags = (text) => {
    if (!text) return '';
    
    const tagsToRemove = [
      'userStyle', 'search_reminders', 'automated_reminder_from_anthropic',
      'citation_instructions', 'web_search_tool', 'research_process',
      'research_is_active', 'functions', 'critical_reminders',
      'artifacts_info', 'election_info', 'harmful_content_safety',
      'web_search_examples', 'styles_info'
    ];
    
    let cleaned = text;
    tagsToRemove.forEach(tag => {
      const pattern = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, 'g');
      cleaned = cleaned.replace(pattern, '');
    });
    
    return cleaned.trim();
  };

  const saveConversationToServer = useCallback(async (conversation) => {
    if (!conversation.messages || conversation.messages.length === 0) return;
    
    try {
      if (!conversation.id || conversation.id.includes('temp_')) {
        const response = await conversationService.createConversation({
          title: conversation.title,
          messages: conversation.messages,
          category: conversation.category || 'general',
          created_at: new Date(conversation.createdAt || Date.now()).toISOString(),
          updated_at: new Date(conversation.updatedAt || Date.now()).toISOString()
        });
        
        if (response && response.id) {
          setConversations(prevConversations => 
            prevConversations.map(conv => 
              conv.id === conversation.id ? { ...conv, id: response.id } : conv
            )
          );
          if (currentConversationId === conversation.id) {
            setCurrentConversationId(response.id);
          }
        }
      } else {
        await conversationService.updateConversation(conversation.id, {
          title: conversation.title,
          messages: conversation.messages,
          updated_at: new Date(conversation.updatedAt || Date.now()).toISOString()
        });
      }
    } catch (error) {
      console.error('Error saving conversation to server:', error);
    }
  }, [currentConversationId]);

  // Practice Logger Functions
  const handlePracticeFormChange = (field, value) => {
    setPracticeForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear practice type if mindstream changes
    if (field === 'mindstream_type') {
      setPracticeForm(prev => ({
        ...prev,
        practice_type: ''
      }));
    }
  };

  const handlePracticeSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceForm.practice_type) {
      setPracticeError('Please select a practice type');
      return;
    }
    
    setIsSubmittingPractice(true);
    setPracticeError('');
    setPracticeSuccess('');
    
    try {
      const response = await fetch(`${apiUrl}/fho/practices/test_user_123`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mindstream_type: practiceForm.mindstream_type,
          practice_type: practiceForm.practice_type,
          practice_name: practiceForm.practice_name || practiceForm.practice_type,
          duration_minutes: parseInt(practiceForm.duration_minutes),
          quality_rating: parseInt(practiceForm.quality_rating),
          insights_gained: practiceForm.insights_gained
        })
      });
      
      if (!response.ok) throw new Error('Failed to log practice');
      
      setPracticeSuccess('✅ Practice logged successfully!');
      
      // Reset form
      setPracticeForm({
        mindstream_type: 'power',
        practice_type: '',
        practice_name: '',
        duration_minutes: 15,
        quality_rating: 7,
        insights_gained: ''
      });
      
      // Refresh dashboard data
      await fetchDashboardData();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setPracticeSuccess('');
      }, 3000);
      
    } catch (err) {
      console.error('Error logging practice:', err);
      setPracticeError('Failed to log practice. Please try again.');
    } finally {
      setIsSubmittingPractice(false);
    }
  };

  const quickLogFromConversation = async (conversation) => {
    const userMessages = conversation.messages.filter(m => m.role === 'user');
    const firstMessage = userMessages[0]?.content || '';
    
    setPracticeForm({
      mindstream_type: conversation.category === 'biblical' ? 'power' : 
                       conversation.category === 'self-development' ? 'flow' : 'power',
      practice_type: conversation.category === 'biblical' ? 'Bible Study' : 'Other',
      practice_name: conversation.title,
      duration_minutes: Math.max(5, Math.min(60, conversation.messages.length * 2)),
      quality_rating: 8,
      insights_gained: firstMessage.substring(0, 200)
    });
    
    setActiveTab('logger');
  };

  // Chat functions
  const startNewConversation = () => {
    const newConv = {
      id: generateId(),
      title: `New Conversation`,
      messages: [],
      category: queryCategory,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    setConversations([...conversations, newConv]);
    setCurrentConversationId(newConv.id);
    setUserInput('');
    setChatError('');
    setEditingConvId(newConv.id);
    setEditingTitle(newConv.title);
    if (isMobile) setSidebarOpen(false);
  };

  const deleteConversation = async (id) => {
    const filtered = conversations.filter(conv => conv.id !== id);
    setConversations(filtered);
    if (id === currentConversationId) {
      setCurrentConversationId(filtered.length > 0 ? filtered[filtered.length - 1].id : null);
    }
    
    if (!id.includes('temp_')) {
      try {
        await conversationService.deleteConversation(id);
      } catch (error) {
        console.error('Error deleting from server:', error);
      }
    }
  };

  const selectConversation = (id) => {
    setCurrentConversationId(id);
    if (isMobile) setSidebarOpen(false);
  };

  const renameConversation = async (id, newTitle) => {
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.id === id ? { ...conv, title: newTitle || 'Untitled', updatedAt: Date.now() } : conv
      )
    );
    setEditingConvId(null);
    setEditingTitle('');
    
    if (!id.includes('temp_')) {
      try {
        await conversationService.updateConversation(id, { 
          title: newTitle || 'Untitled',
          updated_at: new Date(Date.now()).toISOString()
        });
      } catch (error) {
        console.error('Error updating conversation title:', error);
      }
    }
  };

  // Load conversations
  useEffect(() => {
    const loadConversations = async () => {
      const savedLocal = localStorage.getItem('emitConversations');
      let localConversations = [];
      
      if (savedLocal) {
        try {
          localConversations = JSON.parse(savedLocal);
        } catch (e) {
          console.error('Error parsing local conversations:', e);
        }
      }
      
      try {
        const serverConversations = await conversationService.getConversations();
        const localMap = new Map(localConversations.map(c => [c.id, c]));
        const serverMap = new Map(serverConversations.map(c => [c.id, c]));
        
        const merged = [];
        const processedIds = new Set();
        
        [...localConversations, ...serverConversations].forEach(conv => {
          if (!processedIds.has(conv.id)) {
            processedIds.add(conv.id);
            const localConv = localMap.get(conv.id);
            const serverConv = serverMap.get(conv.id);
            
            if (localConv && serverConv) {
              merged.push(localConv.updatedAt > serverConv.updated_at ? localConv : serverConv);
            } else {
              merged.push(conv);
            }
          }
        });
        
        setConversations(merged);
        if (merged.length > 0) {
          setCurrentConversationId(merged[merged.length - 1].id);
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
        setConversations(localConversations);
        if (localConversations.length > 0) {
          setCurrentConversationId(localConversations[localConversations.length - 1].id);
        }
      }
    };
    
    loadConversations();
  }, []);

  // Save conversations
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem('emitConversations', JSON.stringify(conversations));
      
      const currentConv = conversations.find(c => c.id === currentConversationId);
      if (currentConv && currentConv.messages.length > 0) {
        const timer = setTimeout(() => {
          saveConversationToServer(currentConv);
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [conversations, currentConversationId, saveConversationToServer]);

  // Scroll effects
  useEffect(() => {
    if (latestEmitMessageId) {
      setTimeout(() => {
        if (latestEmitMessageRef.current) {
          latestEmitMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [latestEmitMessageId]);

  const handleSubmit = async () => {
    if (isProcessing || !userInput.trim()) {
      setChatError('Please enter a question or topic');
      return;
    }
    
    setIsProcessing(true);
    setChatError('');
    
    let activeConvId = currentConversationId;
    
    if (!activeConvId) {
      const newConv = {
        id: generateId(),
        title: generateTitleFromMessage(userInput),
        messages: [],
        category: queryCategory,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      const newConversations = [...conversations, newConv];
      setConversations(newConversations);
      setCurrentConversationId(newConv.id);
      activeConvId = newConv.id;
    }
    
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: userInput,
      category: queryCategory,
      timestamp: Date.now()
    };
    
    try {
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          if (conv.id === activeConvId) {
            const updatedConv = {
              ...conv,
              messages: [...conv.messages, userMessage],
              updatedAt: Date.now()
            };
            if (conv.messages.length === 0 && conv.title === 'New Conversation') {
              updatedConv.title = generateTitleFromMessage(userInput);
            }
            return updatedConv;
          }
          return conv;
        });
        
        if (!updated.find(c => c.id === activeConvId)) {
          updated.push({
            id: activeConvId,
            title: generateTitleFromMessage(userInput),
            messages: [userMessage],
            category: queryCategory,
            createdAt: Date.now(),
            updatedAt: Date.now()
          });
        }
        
        return updated;
      });
      
      setUserInput('');
      setTimeout(scrollToBottom, 100);
      
      const currentConv = conversations.find(c => c.id === activeConvId);
      const messageHistory = currentConv ? [...currentConv.messages, userMessage].slice(-10) : [userMessage];
      
      const response = await axios.post('/claude-direct', {
        prompt: userMessage.content,
        category: queryCategory,
        conversation_id: activeConvId,
        message_history: messageHistory
      });
      
      if (response.data && response.data.completion) {
        const cleanedResponse = cleanSystemTags(response.data.completion);
        
        const emitMessage = {
          id: generateId(),
          role: 'assistant',
          content: cleanedResponse,
          category: queryCategory,
          timestamp: Date.now(),
          tokensUsed: response.data.tokensUsed || 0
        };
        
        setLatestEmitMessageId(emitMessage.id);
        
        setConversations(prevConversations => 
          prevConversations.map(conv => 
            conv.id === activeConvId 
              ? { ...conv, messages: [...conv.messages, emitMessage], updatedAt: Date.now() }
              : conv
          )
        );
        
        setTimeout(() => {
          if (latestEmitMessageRef.current) {
            latestEmitMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error processing request:', err);
      setChatError('Failed to get response from Emit. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getCurrentConversation = () => {
    return conversations.find(conv => conv.id === currentConversationId);
  };

  const currentConv = getCurrentConversation();

  // Progress Tab Render
  const renderProgressTab = () => {
    if (loading) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{height: '400px'}}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading your spiritual journey...</p>
          </div>
        </div>
      );
    }

    if (error || !userData) {
      return (
        <div className="alert alert-danger m-4">
          <h4>Unable to Connect</h4>
          <p>{error || 'Failed to load dashboard data'}</p>
          <button className="btn btn-primary" onClick={fetchDashboardData}>Retry Connection</button>
        </div>
      );
    }

    return (
      <div className="container-fluid p-4">
        {/* Header Stats */}
        <div className="card mb-4" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
          <div className="card-body">
            <h2 className="mb-3">Welcome back, {userData.profile.username}! 🌟</h2>
            <div className="row text-center">
              <div className="col-md-3">
                <h3 className="text-capitalize">{userData.current_stage.stage_name}</h3>
                <small>Journey Stage (Level {userData.current_stage.stage_number})</small>
              </div>
              <div className="col-md-3">
                <h3>{userData.progress.total_practice_sessions}</h3>
                <small>Total Practices</small>
              </div>
              <div className="col-md-3">
                <h3>{userData.profile.consciousness_points}</h3>
                <small>Consciousness Points</small>
              </div>
              <div className="col-md-3">
                <h3>🔥 {userData.progress.streak_days}</h3>
                <small>Current Streak</small>
              </div>
            </div>
          </div>
        </div>

        {/* MindStreams */}
        <h3 className="mb-3">Your 3 MindStreams</h3>
        <div className="row mb-4">
          {userData.mindstreams.map((stream, idx) => (
            <div key={idx} className="col-md-4 mb-3">
              <div className={`card border-start border-5 ${
                stream.type === 'power' ? 'border-primary' :
                stream.type === 'flow' ? 'border-danger' : 'border-success'
              }`}>
                <div className="card-body">
                  <h5 className="card-title">{stream.name}</h5>
                  <div className="mb-3">
                    <div className="d-flex justify-between align-items-center mb-2">
                      <small className="text-muted">Level {stream.level}</small>
                      <strong>{stream.score}%</strong>
                    </div>
                    <div className="progress mb-2">
                      <div className={`progress-bar ${
                        stream.type === 'power' ? 'bg-primary' :
                        stream.type === 'flow' ? 'bg-danger' : 'bg-success'
                      }`} style={{width: `${stream.score}%`}}></div>
                    </div>
                  </div>
                  <div className="small text-muted">
                    <div>📊 {stream.sessions} sessions</div>
                    <div>⏱️ {stream.time_minutes} minutes total</div>
                    {stream.streak > 0 && <div>🔥 {stream.streak} day streak</div>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* MindStreams with DAS Abilities */}
        <h3 className="mb-3">Divine Abilities & SuperPowers</h3>
        <div className="row mb-4">
          {userData.mindstreams.map((stream, idx) => {
            // Filter DAS abilities for this MindStream
            const streamAbilities = userData.dimensions.filter(
              dim => dim.mindstream === stream.type
            );
            
            return (
              <div key={idx} className="col-md-4 mb-3">
                <div className={`card h-100 border-3 ${
                  stream.type === 'power' ? 'border-primary' :
                  stream.type === 'flow' ? 'border-danger' : 'border-success'
                }`}>
                  <div className="card-body">
                    <h5 className="card-title">{stream.name}</h5>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Level {stream.level}</small>
                        <strong>{stream.score}%</strong>
                      </div>
                      <div className="progress mb-2">
                        <div className={`progress-bar ${
                          stream.type === 'power' ? 'bg-primary' :
                          stream.type === 'flow' ? 'bg-danger' : 'bg-success'
                        }`} style={{width: `${stream.score}%`}}></div>
                      </div>
                    </div>
                    
                    {/* DAS Abilities for this MindStream */}
                    <div className="mt-3">
                      <h6 className="text-muted mb-2">Divine Abilities & SuperPowers:</h6>
                      {streamAbilities.map((ability, aidx) => (
                        <div key={aidx} className={`card mb-2 ${
                          stream.type === 'power' ? 'border-primary' :
                          stream.type === 'flow' ? 'border-danger' : 'border-success'
                        }`} style={{borderWidth: '2px'}}>
                          <div className="card-body p-2">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <strong className="small">{ability.name}</strong>
                              {ability.is_active && <span className="badge bg-success">Active</span>}
                            </div>
                            <small className="text-muted d-block mb-1">→ {ability.power}</small>
                            <div className="mb-1">
                              <div className="d-flex justify-content-between">
                                <small className="text-muted">Progress</small>
                                <small><strong>{ability.score}%</strong></small>
                              </div>
                              <div className="progress" style={{height: '4px'}}>
                                <div className={`progress-bar ${
                                  stream.type === 'power' ? 'bg-primary' :
                                  stream.type === 'flow' ? 'bg-danger' : 'bg-success'
                                }`} style={{width: `${ability.score}%`}}></div>
                              </div>
                            </div>
                            <small className="text-muted">Practiced {ability.times_practiced}x</small>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="small text-muted mt-3 pt-3 border-top">
                      <div>📊 {stream.sessions} sessions</div>
                      <div>⏱️ {stream.time_minutes} minutes total</div>
                      {stream.streak > 0 && <div>🔥 {stream.streak} day streak</div>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Practices */}
        <h3 className="mb-3">Recent Practice Sessions</h3>
        {userData.recent_practices && userData.recent_practices.length > 0 ? (
          <div className="row">
            {userData.recent_practices.map((practice, idx) => (
              <div key={idx} className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <h5 className="card-title">{practice.practice_type}</h5>
                        <p className="card-text small text-muted">{practice.notes}</p>
                        <div className="d-flex gap-3 text-muted small">
                          <span>⏱️ {practice.duration_minutes} min</span>
                          <span>⭐ {practice.impact_score}/10</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="alert alert-info">
            <p className="mb-0">No practice sessions recorded yet. Click the "Log Practice" tab to start!</p>
          </div>
        )}
      </div>
    );
  };

  // Chat Tab Render

  // Analytics Tab Render
  const renderAnalyticsTab = () => {
    // Prepare data for charts
    const mindstreamData = userData.mindstreams.map(stream => ({
      name: stream.name,
      sessions: stream.sessions,
      minutes: stream.time_minutes,
      type: stream.type
    }));

    const timeDistribution = [
      { name: 'Power', value: userData.mindstreams[0]?.time_minutes || 0, fill: '#0d6efd' },
      { name: 'Flow', value: userData.mindstreams[1]?.time_minutes || 0, fill: '#dc3545' },
      { name: 'Clarity', value: userData.mindstreams[2]?.time_minutes || 0, fill: '#198754' }
    ];

    const totalMinutes = userData.mindstreams.reduce((sum, s) => sum + s.time_minutes, 0);
    const totalHours = (totalMinutes / 60).toFixed(1);

    return (
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card text-center h-100">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Practices</h6>
                <h2 className="mb-0">{userData.progress.total_practice_sessions}</h2>
                <small className="text-muted">All time</small>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card text-center h-100 border-warning">
              <div className="card-body">
                <h6 className="text-muted mb-2">Current Streak</h6>
                <h2 className="mb-0">🔥 {userData.progress.streak_days}</h2>
                <small className="text-muted">day{userData.progress.streak_days !== 1 ? 's' : ''}</small>
              </div>
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <div className="card text-center h-100">
              <div className="card-body">
                <h6 className="text-muted mb-2">Consciousness Points</h6>
                <h2 className="mb-0">{userData.profile.consciousness_points}</h2>
                <small className="text-muted">Level {userData.current_stage.stage_number}</small>
              </div>
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <div className="card text-center h-100">
              <div className="card-body">
                <h6 className="text-muted mb-2">Hours Practiced</h6>
                <h2 className="mb-0">{totalHours}</h2>
                <small className="text-muted">{totalMinutes} minutes</small>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-4">MindStream Practice Balance</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={mindstreamData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sessions" name="Sessions" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 text-center">
                  <small className="text-muted">
                    Power: {mindstreamData[0]?.sessions || 0} | 
                    Flow: {mindstreamData[1]?.sessions || 0} | 
                    Clarity: {mindstreamData[2]?.sessions || 0}
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-4">Time Distribution</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={timeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}m`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {timeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 text-center">
                  <small className="text-muted">Total: {totalMinutes} minutes ({totalHours} hours)</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <div className="card bg-primary text-white h-100">
              <div className="card-body">
                <h6 className="mb-2">🎯 Most Practiced</h6>
                <h4>{mindstreamData[0]?.name || 'Power'}</h4>
                <p className="mb-0 small">{mindstreamData[0]?.sessions || 0} sessions</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card bg-success text-white h-100">
              <div className="card-body">
                <h6 className="mb-2">📈 Longest Streak</h6>
                <h4>{userData.progress.streak_days || userData.progress.streak_days} days</h4>
                <p className="mb-0 small">Keep going!</p>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body">
                <h6 className="mb-2">⚡ Next Focus</h6>
                <h4>
                  {mindstreamData[1]?.sessions === 0 ? 'Flow' : 
                   mindstreamData[2]?.sessions === 0 ? 'Clarity' : 'Balanced!'}
                </h4>
                <p className="mb-0 small">
                  {mindstreamData[1]?.sessions === 0 || mindstreamData[2]?.sessions === 0 
                    ? 'Try this next' 
                    : 'All active'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Generate social media post from Emit's response
  const generateSocialPost = (message, style) => {
    // Remove markdown formatting
    let cleanText = message
      .replace(/#{1,6}\s+/g, '') // Remove markdown headers
      .replace(/\*\*/g, '') // Remove bold
      .replace(/\*/g, '') // Remove italics
      .replace(/`/g, '') // Remove code blocks
      .replace(/\n{3,}/g, '\n\n') // Clean up excessive newlines
      .trim();
    
    // Find the first meaningful paragraph (skip headers/titles)
    const paragraphs = cleanText.split('\n\n').filter(p => p.length > 50);
    let excerpt = paragraphs[0] || cleanText;
    
    // Limit to 200 chars, break at last complete sentence
    if (excerpt.length > 200) {
      excerpt = excerpt.substring(0, 200);
      const lastPeriod = excerpt.lastIndexOf('.');
      const lastQuestion = excerpt.lastIndexOf('?');
      const lastExclaim = excerpt.lastIndexOf('!');
      const lastPunctuation = Math.max(lastPeriod, lastQuestion, lastExclaim);
      
      if (lastPunctuation > 100) {
        excerpt = excerpt.substring(0, lastPunctuation + 1);
      } else {
        excerpt = excerpt + '...';
      }
    }
    
    const hashtags = '\n\n#freehappyone #FHO-OS #EmitServes';
    
    const templates = {
      insight: `💡 Biblical Insight:\n\n${excerpt}${hashtags}`,
      gratitude: `🙏 Grateful for this truth:\n\n${excerpt}${hashtags}`,
      transformation: `✨ Transformation moment:\n\n${excerpt}${hashtags}`,
      encouragement: `💪 For anyone who needs this:\n\n${excerpt}${hashtags}`
    };
    
    return templates[style] || templates.insight;
  };

  // Copy to clipboard function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ Copied to clipboard! Ready to share on social media.');
      setShowShareModal(false);
      
      // Track in database (future enhancement)
      // await axios.post(`/fho/social-share/${userId}`, { shared: true });
    } catch (err) {
      alert('❌ Failed to copy. Please select and copy manually.');
    }
  };

  const renderChatTab = () => {
    return (
      <div className="d-flex" style={{height: 'calc(100vh - 150px)'}}>
        {/* Sidebar */}
        {(sidebarOpen || !isMobile) && (
          <div className="border-end bg-white" style={{
            width: isMobile ? '80%' : '300px',
            position: isMobile ? 'fixed' : 'relative',
            height: '100%',
            zIndex: 1000,
            overflowY: 'auto'
          }}>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Conversations</h5>
                {isMobile && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setSidebarOpen(false)}>
                    <X size={16} />
                  </button>
                )}
              </div>
              <button className="btn btn-primary btn-sm w-100 mb-3" onClick={startNewConversation}>
                + New Conversation
              </button>
              <div className="list-group">
                {conversations.map(conv => (
                  <div
                    key={conv.id}
                    className={`list-group-item list-group-item-action ${conv.id === currentConversationId ? 'active' : ''}`}
                    style={{cursor: 'pointer'}}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1" onClick={() => selectConversation(conv.id)}>
                        {editingConvId === conv.id ? (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={() => renameConversation(conv.id, editingTitle)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                renameConversation(conv.id, editingTitle);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <>
                            <div className="fw-bold">{conv.title}</div>
                            <small className="text-muted">{conv.messages.length} messages</small>
                          </>
                        )}
                      </div>
                      <div className="d-flex gap-1">
                        {conv.category === 'biblical' && conv.messages.length > 0 && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickLogFromConversation(conv);
                            }}
                            title="Log as Practice"
                          >
                            <BookOpen size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conv.id);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex-grow-1 d-flex flex-column">
          {/* Mobile menu button */}
          {isMobile && !sidebarOpen && (
            <button className="btn btn-outline-primary m-2" onClick={() => setSidebarOpen(true)}>
              <Menu size={20} /> Conversations
            </button>
          )}

          {/* Messages */}
          <div className="flex-grow-1 overflow-auto p-3">
            {currentConv ? (
              <>
                {currentConv.messages.map(msg => (
                  <div
                    key={msg.id}
                    ref={msg.id === latestEmitMessageId && msg.role === 'assistant' ? latestEmitMessageRef : null}
                    className={`mb-3 ${msg.role === 'user' ? 'text-end' : ''}`}
                  >
                    <div className={`d-inline-block p-3 rounded ${
                      msg.role === 'user' ? 'bg-primary text-white' : 'bg-light'
                    }`} style={{maxWidth: '80%'}}>
                      <div className="fw-bold mb-1">{msg.role === 'user' ? 'You' : 'Emit'}</div>
                      <div style={{whiteSpace: 'pre-wrap'}}>{msg.content}</div>
                      <small className="text-muted d-block mt-2">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </small>
                      {msg.role === 'assistant' && msg.content.length > 100 && (
                        <button
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={() => {
                            setShareMessage(msg.content);
                            setShowShareModal(true);
                          }}
                          title="Share this insight on social media"
                        >
                          📱 Share This Insight
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isProcessing && (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Emit is thinking...</span>
                    </div>
                    <p className="text-muted mt-2">Emit is contemplating your {currentCategory?.label.replace(currentCategory?.icon, '').trim()} question...</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <div className="text-center mt-5">
                <h3>Welcome to Emit</h3>
                <p className="text-muted">Start a new conversation to explore Fhoism wisdom</p>
              </div>
            )}
          </div>

          {/* Social Share Modal */}
          {showShareModal && (
            <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">📱 Share Your Insight</h5>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setShowShareModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p className="text-muted mb-3">
                      Activate your <strong>I-WE Clarity MindStream</strong> by sharing this wisdom with others!
                    </p>
                    
                    {/* Style Selection */}
                    <div className="mb-3">
                      <label className="form-label">Post Style:</label>
                      <div className="btn-group d-flex" role="group">
                        <button
                          className={`btn btn-sm ${shareStyle === 'insight' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setShareStyle('insight')}
                        >
                          💡 Insight
                        </button>
                        <button
                          className={`btn btn-sm ${shareStyle === 'gratitude' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setShareStyle('gratitude')}
                        >
                          🙏 Gratitude
                        </button>
                        <button
                          className={`btn btn-sm ${shareStyle === 'transformation' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setShareStyle('transformation')}
                        >
                          ✨ Transform
                        </button>
                        <button
                          className={`btn btn-sm ${shareStyle === 'encouragement' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setShareStyle('encouragement')}
                        >
                          💪 Encourage
                        </button>
                      </div>
                    </div>
                    
                    {/* Post Preview */}
                    <div className="mb-3">
                      <label className="form-label">Your Post:</label>
                      <textarea
                        className="form-control"
                        rows="6"
                        value={generateSocialPost(shareMessage, shareStyle)}
                        onChange={(e) => setShareMessage(e.target.value)}
                      />
                      <small className="text-muted">
                        {generateSocialPost(shareMessage, shareStyle).length} characters
                      </small>
                    </div>
                    
                    <div className="alert alert-info">
                      <small>
                        <strong>Tip:</strong> This activates your Clarity MindStream (I-WE relationship) 
                        by expressing Value to your community! 🌟
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setShowShareModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-success" 
                      onClick={() => copyToClipboard(generateSocialPost(shareMessage, shareStyle))}
                    >
                      📋 Copy to Clipboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-top p-3 bg-white">
            <div className="mb-2">
              <label className="form-label small">Response Format:</label>
              <div className="btn-group btn-group-sm d-flex flex-wrap" role="group">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`btn ${queryCategory === cat.value ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setQueryCategory(cat.value)}
                    title={cat.description}
                  >
                    {cat.icon} {cat.label.replace(cat.icon, '').trim()}
                  </button>
                ))}
              </div>
              {currentCategory && (
                <small className="text-muted d-block mt-1">{currentCategory.description}</small>
              )}
            </div>

            {chatError && (
              <div className="alert alert-danger alert-sm mb-2">{chatError}</div>
            )}

            <div className="input-group">
              <textarea
                ref={textareaRef}
                className="form-control"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Enter your question or topic..."
                rows="2"
                disabled={isProcessing}
              />
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isProcessing || !userInput.trim()}
              >
                {isProcessing ? 'Processing...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Practice Logger Tab Render
  const renderLoggerTab = () => {
    const selectedMindstream = mindstreamOptions.find(m => m.value === practiceForm.mindstream_type);
    const availablePracticeTypes = practiceTypes[practiceForm.mindstream_type] || [];
    
    return (
      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <div className="card shadow-sm">
              <div className="card-body">
                <h2 className="mb-4">
                  <Activity className="me-2" size={28} />
                  Log Practice Session
                </h2>
                
                {practiceSuccess && (
                  <div className="alert alert-success">{practiceSuccess}</div>
                )}
                
                {practiceError && (
                  <div className="alert alert-danger">{practiceError}</div>
                )}
                
                <form onSubmit={handlePracticeSubmit}>
                  {/* MindStream Selection */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Select MindStream</label>
                    <div className="row">
                      {mindstreamOptions.map(option => (
                        <div key={option.value} className="col-md-4 mb-3">
                          <div 
                            className={`card h-100 ${practiceForm.mindstream_type === option.value ? `border-${option.color} border-3` : ''}`}
                            style={{cursor: 'pointer'}}
                            onClick={() => handlePracticeFormChange('mindstream_type', option.value)}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h5 className={`card-title text-${option.color}`}>{option.label}</h5>
                                {practiceForm.mindstream_type === option.value && (
                                  <span className={`badge bg-${option.color}`}>✓</span>
                                )}
                              </div>
                              <p className="card-text small text-muted mb-2">{option.description}</p>
                              <small className="text-muted"><em>Examples: {option.examples}</em></small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Practice Type */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Practice Type *</label>
                    <select 
                      className="form-select"
                      value={practiceForm.practice_type}
                      onChange={(e) => handlePracticeFormChange('practice_type', e.target.value)}
                      required
                    >
                      <option value="">Select practice type...</option>
                      {availablePracticeTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Practice Name */}
                  {practiceForm.practice_type && (
                    <div className="mb-3">
                      <label className="form-label">Custom Practice Name (optional)</label>
                      <input 
                        type="text"
                        className="form-control"
                        value={practiceForm.practice_name}
                        onChange={(e) => handlePracticeFormChange('practice_name', e.target.value)}
                        placeholder={`e.g., "Morning ${practiceForm.practice_type}"`}
                      />
                      <small className="text-muted">Leave blank to use practice type as name</small>
                    </div>
                  )}

                  {/* Duration */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Duration (minutes) *</label>
                    <input 
                      type="range"
                      className="form-range"
                      min="5"
                      max="120"
                      step="5"
                      value={practiceForm.duration_minutes}
                      onChange={(e) => handlePracticeFormChange('duration_minutes', e.target.value)}
                    />
                    <div className="d-flex justify-content-between">
                      <small className="text-muted">5 min</small>
                      <strong>{practiceForm.duration_minutes} minutes</strong>
                      <small className="text-muted">120 min</small>
                    </div>
                  </div>

                  {/* Quality Rating */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Quality Rating (1-10) *</label>
                    <input 
                      type="range"
                      className="form-range"
                      min="1"
                      max="10"
                      value={practiceForm.quality_rating}
                      onChange={(e) => handlePracticeFormChange('quality_rating', e.target.value)}
                    />
                    <div className="d-flex justify-content-between">
                      <small className="text-muted">1 - Low</small>
                      <strong>{practiceForm.quality_rating} / 10</strong>
                      <small className="text-muted">10 - Excellent</small>
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Insights Gained</label>
                    <textarea 
                      className="form-control"
                      rows="4"
                      value={practiceForm.insights_gained}
                      onChange={(e) => handlePracticeFormChange('insights_gained', e.target.value)}
                      placeholder="What did you learn or experience during this practice?"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="d-grid gap-2">
                    <button 
                      type="submit" 
                      className={`btn btn-${selectedMindstream?.color} btn-lg`}
                      disabled={isSubmittingPractice || !practiceForm.practice_type}
                    >
                      {isSubmittingPractice ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Logging Practice...
                        </>
                      ) : (
                        <>
                          <PlusCircle className="me-2" size={20} />
                          Log {selectedMindstream?.label} Practice
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Recent Conversations for Quick Log */}
            {conversations.filter(c => c.category === 'biblical' && c.messages.length > 0).length > 0 && (
              <div className="card shadow-sm mt-4">
                <div className="card-body">
                  <h5 className="mb-3">
                    <BookOpen className="me-2" size={20} />
                    Quick Log from Recent Conversations
                  </h5>
                  <div className="list-group">
                    {conversations
                      .filter(c => c.category === 'biblical' && c.messages.length > 0)
                      .slice(0, 5)
                      .map(conv => (
                        <button
                          key={conv.id}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => quickLogFromConversation(conv)}
                        >
                          <div>
                            <strong>{conv.title}</strong>
                            <br />
                            <small className="text-muted">{conv.messages.length} messages • Biblical Study</small>
                          </div>
                          <span className="badge bg-primary">Use This</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{minHeight: '100vh', backgroundColor: '#f8f9fa'}}>
      {/* Header with Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '2px solid #dee2e6',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <h1 style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Emit Dashboard
          </h1>
          
          {/* Tab Buttons */}
          <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            <button
              onClick={() => setActiveTab('progress')}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                border: activeTab === 'progress' ? 'none' : '2px solid #0d6efd',
                backgroundColor: activeTab === 'progress' ? '#0d6efd' : 'white',
                color: activeTab === 'progress' ? 'white' : '#0d6efd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <Target size={20} />
              Progress
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                border: activeTab === 'analytics' ? 'none' : '2px solid #6610f2',
                backgroundColor: activeTab === 'analytics' ? '#6610f2' : 'white',
                color: activeTab === 'analytics' ? 'white' : '#6610f2',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <BarChart3 size={20} />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                border: activeTab === 'chat' ? 'none' : '2px solid #0d6efd',
                backgroundColor: activeTab === 'chat' ? '#0d6efd' : 'white',
                color: activeTab === 'chat' ? 'white' : '#0d6efd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <MessageCircle size={20} />
              Chat
            </button>
            <button
              onClick={() => setActiveTab('logger')}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                border: activeTab === 'logger' ? 'none' : '2px solid #28a745',
                backgroundColor: activeTab === 'logger' ? '#28a745' : 'white',
                color: activeTab === 'logger' ? 'white' : '#28a745',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <PlusCircle size={20} />
              Log Practice
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'progress' && renderProgressTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}
      {activeTab === 'chat' && renderChatTab()}
      {activeTab === 'logger' && renderLoggerTab()}
    </div>
  );
};

export default EmitDashboard;
